import {
  all,
  put,
  call,
  select,
  debounce,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
// features
import { receivedSwitchBranchSuccess } from '../branch/actions';
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { makeSelectLoadDetails } from '../dispatch-details/load/selectors';
import { createTelRateActionSuccess, updateTelRateActionSuccess } from '../rate/actions';
import {
  getLoadDetailsSuccess,
  updateAppointmentSuccess,
  updateLoadDetailsSuccess,
} from '../dispatch-details/load/actions';
import {
  getTelNotificationsRequest,
  createPendingActivitySuccess,
  updatePendingActivitySuccess,
  removePendingActivitySuccess,
  createUnavailablePeriodSuccess,
  updateUnavailablePeriodSuccess,
  removeUnavailablePeriodSuccess,
} from '../drivers-card/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// sagas
import { visitPageSaga as visitAllowedPageSaga } from '../../sagas';
// feature driver-cards-v2
import * as A from './actions';
import { isRouteBuilderLoad, getRouteBuilderGuid } from './edit-locks';
import {
  getResourceGroupKey,
  createResourceGroupingState,
  createLoadedResourceGrouping,
} from './grouping';
import {
  getRequestError,
  getDriverCardsV2Defaults,
  getUnassignedLoadsRequestData,
  getAssignedResourcesRequestData,
} from './helpers';
import {
  normalizeLoad,
  getCloEventGuids,
  getEventReferencesByGuid,
  getDriverCardsUpdateRequest,
  getUnassignedLoadsFromResponse,
  getAssignedResourcesFromResponse,
} from './api/adapters';
import {
  requestLoadDetails,
  requestEventReferences,
  requestUnassignedLoads,
  requestUpdateDriverCards,
  requestAssignedResources,
  requestResourceGroupInfo,
  requestDriverCardsConfigs,
} from './api/requests';
import {
  makeSelectShowTeam,
  makeSelectEditSession,
  makeSelectResourceMode,
  makeSelectResourceFilter,
  makeSelectCustomerFilter,
  makeSelectResourceSearch,
  makeSelectResourceGroupBy,
  makeSelectResourceGrouping,
  makeSelectRequestDateRange,
  makeSelectLoadReferenceFilter,
} from './selectors';
//////////////////////////////////////////////////

const DRIVER_CARDS_V2_CONFIG_NAMES = [
  GC.UI_DRIVER_CARDS_SHOW_TEAM_DRIVER,
  GC.UI_DRIVER_CARDS_DEFAULT_GROUP_BY,
  GC.UI_DRIVER_CARDS_DEFAULT_DAY_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_GROUP_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_DRIVER_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_SEARCH_RADIUS,
];

const getEditLoadSyncError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-refresh-changed-trip',
  'Unable to refresh the changed trip.',
);

const getUnassignedLoadsError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-load-unassigned-loads',
  'Unable to load unassigned loads.',
);

const getAssignedResourcesError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-load-assigned-resources',
  'Unable to load driver or truck resources.',
);

const getResourceGroupInfoError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-load-resource-groups',
  'Unable to load driver or truck groups.',
);

const getSaveEditSessionError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-save-changes',
  'Unable to save Driver Cards changes.',
);

const getEditReferencesError = () => G.getWindowLocale(
  'messages:driver-cards-v2-unable-to-load-stop-references',
  'Unable to load stop references. Changes were not saved.',
);

export function* visitPageSaga({ payload }) {
  yield call(visitAllowedPageSaga, payload, GC.CHECK_VISIT_DRIVER_CARDS_PAGE_V2);
  yield put(A.setPageVisited(true));
  yield put(A.loadPageDefaultsRequest());
}

export function* loadPageDefaultsSaga() {
  let configs = {};

  try {
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    const response = yield call(
      requestDriverCardsConfigs,
      branchGuid,
      DRIVER_CARDS_V2_CONFIG_NAMES,
    );

    if (G.isResponseSuccess(response.status)) {
      configs = G.mapConfigValuesByName(response.data);
    } else {
      yield call(G.handleFailResponse, response, 'loadDriverCardsV2PageDefaultsSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'loadDriverCardsV2PageDefaultsSaga exception');
  }

  yield put(A.applyPageDefaults(getDriverCardsV2Defaults(configs)));
  yield put(A.loadPageDataRequest());
}

export function* loadPageDataSaga() {
  yield all([
    put(A.loadAssignedResourcesRequest()),
    put(A.loadResourceGroupInfoRequest()),
    put(A.loadUnassignedLoadsRequest()),
  ]);
}

export function* saveEditSessionSaga() {
  const [branchGuid, editSession] = yield all([
    select(makeSelectCurrentBranchGuid()),
    select(makeSelectEditSession()),
  ]);

  try {
    console.log('[Driver Cards V2] Full edit draft', editSession); // eslint-disable-line no-console

    const eventGuids = getCloEventGuids(editSession.draftLoads
      .filter((load) => !isRouteBuilderLoad(load))
      .flatMap((load) => (load.events || []).map(({ source }) => source || {})));

    let referencesByEventGuid = {};

    if (eventGuids.length) {
      const referencesResponse = yield call(requestEventReferences, eventGuids);

      if (!G.isResponseSuccess(referencesResponse.status)) {
        yield put(A.saveEditSessionFailure(getEditReferencesError()));
        yield call(G.handleFailResponse, referencesResponse, 'loadDriverCardsV2StopReferences fail');

        return;
      }

      referencesByEventGuid = getEventReferencesByGuid(referencesResponse.data);

      if (!referencesByEventGuid) {
        yield put(A.saveEditSessionFailure(getEditReferencesError()));

        return;
      }
    }

    const data = getDriverCardsUpdateRequest(branchGuid, editSession, referencesByEventGuid);

    console.log('[Driver Cards V2] Adapted PUT request', data); // eslint-disable-line no-console

    const response = yield call(requestUpdateDriverCards, { data });

    if (G.isResponseSuccess(response.status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');
      yield put(A.discardEditSession());
      yield put(A.loadPageDataRequest());

      return;
    }

    yield put(A.saveEditSessionFailure(getRequestError(response, getSaveEditSessionError())));
    yield call(G.handleFailResponse, response, 'saveDriverCardsV2EditSessionSaga fail');
  } catch (error) {
    yield put(A.saveEditSessionFailure(getSaveEditSessionError()));
    yield call(G.handleException, error, 'saveDriverCardsV2EditSessionSaga exception');
  }
}

export function* loadAssignedResourcesSaga() {
  try {
    const [
      branchGuid,
      showTeam,
      resourceMode,
      resourceFilter,
      resourceGroupBy,
      resourceSearch,
      requestDateRange,
      customerFilter,
      loadReferenceFilter,
    ] = yield all([
      select(makeSelectCurrentBranchGuid()),
      select(makeSelectShowTeam()),
      select(makeSelectResourceMode()),
      select(makeSelectResourceFilter()),
      select(makeSelectResourceGroupBy()),
      select(makeSelectResourceSearch()),
      select(makeSelectRequestDateRange()),
      select(makeSelectCustomerFilter()),
      select(makeSelectLoadReferenceFilter()),
    ]);

    if (
      resourceGroupBy !== ENUMS.ENUM_NONE
      && G.isNilOrEmpty(resourceSearch)
    ) {
      yield put(A.loadAssignedResourcesSuccess({
        items: [],
        grouping: createResourceGroupingState(),
      }));

      return;
    }

    const requestGroupBy = resourceGroupBy === ENUMS.ENUM_GROUP_AND_DISPATCHER
      ? ENUMS.ENUM_DISPATCHING_GROUP
      : resourceGroupBy;

    const options = {
      data: getAssignedResourcesRequestData({
        showTeam,
        branchGuid,
        resourceFilter,
        resourceSearch,
        customerFilter,
        requestDateRange,
        loadReferenceFilter,
        resourceGroupBy: requestGroupBy,
      }),
    };

    const response = yield call(requestAssignedResources, resourceMode, resourceGroupBy, options);
    const { data, status } = response;

    if (G.isResponseSuccess(status)) {
      const resources = getAssignedResourcesFromResponse(data, resourceMode, resourceGroupBy);
      const telGuids = resources.flatMap(({ loads }) => loads.map(({ id }) => id));

      const grouping = resourceGroupBy === ENUMS.ENUM_NONE
        ? createResourceGroupingState()
        : createLoadedResourceGrouping(resources, resourceGroupBy);

      yield put(A.loadAssignedResourcesSuccess({ grouping, items: resources }));

      if (telGuids.length) yield put(getTelNotificationsRequest(telGuids));

      return;
    }

    yield put(A.loadAssignedResourcesFailure(getRequestError(response, getAssignedResourcesError())));
    yield call(G.handleFailResponse, response, 'loadDriverCardsV2AssignedResourcesSaga fail');
  } catch (error) {
    yield put(A.loadAssignedResourcesFailure(getAssignedResourcesError()));
    yield call(G.handleException, error, 'loadDriverCardsV2AssignedResourcesSaga exception');
  }
}

export function* loadResourceGroupInfoSaga() {
  try {
    const [branchGuid, resourceMode] = yield all([
      select(makeSelectCurrentBranchGuid()),
      select(makeSelectResourceMode()),
    ]);

    const response = yield call(requestResourceGroupInfo, resourceMode, branchGuid);
    const { data, status } = response;

    if (G.isResponseSuccess(status)) {
      yield put(A.loadResourceGroupInfoSuccess(data));

      return;
    }

    yield put(A.loadResourceGroupInfoFailure(getRequestError(response, getResourceGroupInfoError())));
    yield call(G.handleFailResponse, response, 'loadDriverCardsV2ResourceGroupInfoSaga fail');
  } catch (error) {
    yield put(A.loadResourceGroupInfoFailure(getResourceGroupInfoError()));
    yield call(G.handleException, error, 'loadDriverCardsV2ResourceGroupInfoSaga exception');
  }
}

export function* loadExpandedResourceGroupSaga({ payload }) {
  const { groupKey, loadResources } = payload;

  const grouping = yield select(makeSelectResourceGrouping());

  if (
    !loadResources
    || !grouping.expandedKeys.includes(groupKey)
    || grouping.loadedKeys.includes(groupKey)
    || grouping.loadingKeys.includes(groupKey)
  ) return;

  yield put(A.loadResourceGroupStart({ groupKey }));

  try {
    const [
      branchGuid,
      showTeam,
      resourceMode,
      resourceFilter,
      resourceGroupBy,
      resourceSearch,
      requestDateRange,
      customerFilter,
      loadReferenceFilter,
    ] = yield all([
      select(makeSelectCurrentBranchGuid()),
      select(makeSelectShowTeam()),
      select(makeSelectResourceMode()),
      select(makeSelectResourceFilter()),
      select(makeSelectResourceGroupBy()),
      select(makeSelectResourceSearch()),
      select(makeSelectRequestDateRange()),
      select(makeSelectCustomerFilter()),
      select(makeSelectLoadReferenceFilter()),
    ]);

    const requestGroupBy = resourceGroupBy === ENUMS.ENUM_GROUP_AND_DISPATCHER
      ? ENUMS.ENUM_DISPATCHING_GROUP
      : resourceGroupBy;

    const options = {
      data: {
        ...getAssignedResourcesRequestData({
          showTeam,
          branchGuid,
          resourceFilter,
          resourceSearch,
          customerFilter,
          requestDateRange,
          loadReferenceFilter,
          resourceGroupBy: requestGroupBy,
        }),
        dispatcherGuid: payload.dispatcherGuid || null,
        dispatchingGroupGuid: payload.groupGuid || null,
      },
    };

    const response = yield call(
      requestAssignedResources,
      resourceMode,
      resourceGroupBy,
      options,
    );

    const { data, status } = response;

    const currentGrouping = yield select(makeSelectResourceGrouping());

    if (!currentGrouping.loadingKeys.includes(groupKey)) return;

    if (G.isResponseSuccess(status)) {
      const resources = getAssignedResourcesFromResponse(data, resourceMode, resourceGroupBy)
        .filter((resource) => getResourceGroupKey(resource, resourceGroupBy) === groupKey);

      const telGuids = resources.flatMap(({ loads }) => loads.map(({ id }) => id));

      yield put(A.loadResourceGroupSuccess({ groupKey, resources }));

      if (telGuids.length) yield put(getTelNotificationsRequest(telGuids));

      return;
    }

    const requestError = getRequestError(response, getAssignedResourcesError());

    yield put(A.loadResourceGroupFailure({ groupKey, error: requestError }));
    yield call(G.handleFailResponse, response, 'loadDriverCardsV2ResourceGroupSaga fail');
  } catch (error) {
    const currentGrouping = yield select(makeSelectResourceGrouping());

    if (!currentGrouping.loadingKeys.includes(groupKey)) return;

    yield put(A.loadResourceGroupFailure({ groupKey, error: getAssignedResourcesError() }));
    yield call(G.handleException, error, 'loadDriverCardsV2ResourceGroupSaga exception');
  }
}

export function* loadUnassignedLoadsSaga({ payload: filters }) {
  try {
    const [branchGuid, requestDateRange] = yield all([
      select(makeSelectCurrentBranchGuid()),
      select(makeSelectRequestDateRange()),
    ]);

    const options = {
      data: getUnassignedLoadsRequestData({ filters, branchGuid, requestDateRange }),
    };

    const response = yield call(requestUnassignedLoads, options);
    const { data, status } = response;

    if (G.isResponseSuccess(status)) {
      const loads = getUnassignedLoadsFromResponse(data);

      yield put(A.loadUnassignedLoadsSuccess(loads));

      return;
    }

    yield put(A.loadUnassignedLoadsFailure(getRequestError(response, getUnassignedLoadsError())));
    yield call(G.handleFailResponse, response, 'loadDriverCardsV2UnassignedLoadsSaga fail');
  } catch (error) {
    yield put(A.loadUnassignedLoadsFailure(getUnassignedLoadsError()));
    yield call(G.handleException, error, 'loadDriverCardsV2UnassignedLoadsSaga exception');
  }
}

export function* refreshEditLoadSaga({ payload }) {
  const { loadId, sessionId } = payload;

  const session = yield select(makeSelectEditSession());

  if (session.sessionId !== sessionId || !session.draftLoads.some(({ id }) => id === loadId)) return;

  const requestId = G.generateGuid();

  yield put(A.refreshEditLoadStart({ loadId, requestId, sessionId }));

  try {
    const response = yield call(requestLoadDetails, loadId);
    const { data, status } = response;

    if (G.isResponseSuccess(status)) {
      const load = { ...normalizeLoad(data), id: loadId };

      yield put(A.refreshEditLoadSuccess({ load, loadId, requestId, sessionId }));

      return;
    }

    yield put(A.refreshEditLoadFailure({
      loadId,
      requestId,
      sessionId,
      error: getRequestError(response, getEditLoadSyncError()),
    }));
    yield call(G.handleFailResponse, response, 'refreshDriverCardsV2EditLoadSaga fail');
  } catch (error) {
    yield put(A.refreshEditLoadFailure({
      loadId,
      requestId,
      sessionId,
      error: getEditLoadSyncError(),
    }));
    yield call(G.handleException, error, 'refreshDriverCardsV2EditLoadSaga exception');
  }
}

export function* refreshRouteBuilderDraftSaga({ payload }) {
  const { routeGuid, sessionId } = payload;

  const session = yield select(makeSelectEditSession());

  if (session.sessionId !== sessionId) return;

  const loadIds = session.draftLoads
    .filter((load) => getRouteBuilderGuid(load) === routeGuid)
    .map(({ id }) => id);

  yield all(loadIds.map((loadId) => put(A.refreshEditLoadRequest({ loadId, sessionId }))));

  yield all([
    put(A.loadAssignedResourcesRequest()),
    put(A.loadUnassignedLoadsRequest()),
  ]);
}

export function* reconcileExpandedLoadDetailsSaga({ payload }) {
  const session = yield select(makeSelectEditSession());
  const loadId = G.getGuidFromObject(payload);

  if (!loadId || !session.draftLoads.some(({ id }) => id === loadId)) return;

  yield put(A.refreshEditLoadSuccess({
    loadId,
    sessionId: session.sessionId,
    load: { ...normalizeLoad(payload), id: loadId },
  }));
}

export function* reloadEditLoadAfterDetailsMutationSaga({ payload }) {
  const [session, details] = yield all([
    select(makeSelectEditSession()),
    select(makeSelectLoadDetails()),
  ]);

  const candidateLoadIds = [
    payload && payload.telGuid,
    payload && payload.loadGuid,
    G.getGuidFromObject(payload),
    G.getGuidFromObject(details),
  ].filter(Boolean);

  const loadId = candidateLoadIds.find((candidate) => (
    session.draftLoads.some(({ id }) => id === candidate)
  ));

  if (!loadId) return;

  yield put(A.refreshEditLoadRequest({ loadId, sessionId: session.sessionId }));
}

export function* reloadAssignedResourcesSaga() {
  yield put(A.loadAssignedResourcesRequest());
}

export function* reloadResourceModeDataSaga() {
  yield all([
    put(A.loadAssignedResourcesRequest()),
    put(A.loadResourceGroupInfoRequest()),
  ]);
}

export function* reloadAssignedResourcesAfterLegacyResourceMutationSaga() {
  if (G.isEqualCurrentPathnameAndRouteName(GC.ROUTE_PATH_DRIVER_CARDS_V2)) {
    yield put(A.loadAssignedResourcesRequest());
  }
}

export function* reloadAssignedResourcesAfterFilterChangeSaga() {
  const editSession = yield select(makeSelectEditSession());

  if (editSession.resourceId) return;

  yield put(A.loadAssignedResourcesRequest());
}

export function* reloadPageDataAfterBranchSwitchSaga() {
  if (G.isEqualCurrentPathnameAndRouteName(GC.ROUTE_PATH_DRIVER_CARDS_V2)) {
    yield put(A.loadPageDefaultsRequest());
  }
}

export function* reloadPageDataAfterRateCreateSaga() {
  if (G.isEqualCurrentPathnameAndRouteName(GC.ROUTE_PATH_DRIVER_CARDS_V2)) {
    yield put(A.loadPageDataRequest());
  }
}

export default function* driverCardsV2WatcherSaga() {
  yield takeLatest(GC.VISIT_DRIVER_CARDS_PAGE_V2, visitPageSaga);
  yield takeLatest(A.loadPageDefaultsRequest, loadPageDefaultsSaga);
  yield takeLatest(A.loadPageDataRequest, loadPageDataSaga);
  yield takeLatest(A.saveEditSessionRequest, saveEditSessionSaga);
  yield takeEvery(A.refreshEditLoadRequest, refreshEditLoadSaga);
  yield takeLatest(A.routeBuilderRouteSaved, refreshRouteBuilderDraftSaga);
  yield takeLatest(getLoadDetailsSuccess, reconcileExpandedLoadDetailsSaga);
  yield takeLatest(
    [updateAppointmentSuccess, updateLoadDetailsSuccess],
    reloadEditLoadAfterDetailsMutationSaga,
  );
  yield takeLatest(A.setRequestDateRange, loadPageDataSaga);
  yield takeLatest(A.setResourceMode, reloadResourceModeDataSaga);
  yield takeLatest(A.setShowTeam, reloadAssignedResourcesSaga);
  yield takeLatest(A.setResourceFilter, reloadAssignedResourcesAfterFilterChangeSaga);
  yield takeLatest(A.setResourceGroupBy, reloadAssignedResourcesAfterFilterChangeSaga);
  yield takeLatest(A.setCustomerFilter, reloadAssignedResourcesAfterFilterChangeSaga);
  yield debounce(350, A.setLoadReferenceFilter, reloadAssignedResourcesAfterFilterChangeSaga);
  yield takeLatest(A.loadAssignedResourcesRequest, loadAssignedResourcesSaga);
  yield takeLatest(A.loadResourceGroupInfoRequest, loadResourceGroupInfoSaga);
  yield takeLatest(A.loadUnassignedLoadsRequest, loadUnassignedLoadsSaga);
  yield takeEvery(A.toggleResourceGroup, loadExpandedResourceGroupSaga);
  yield takeLatest([
    createPendingActivitySuccess,
    updatePendingActivitySuccess,
    removePendingActivitySuccess,
    createUnavailablePeriodSuccess,
    updateUnavailablePeriodSuccess,
    removeUnavailablePeriodSuccess,
  ], reloadAssignedResourcesAfterLegacyResourceMutationSaga);
  yield takeLatest([createTelRateActionSuccess, updateTelRateActionSuccess], reloadPageDataAfterRateCreateSaga);
  yield takeLatest(receivedSwitchBranchSuccess, reloadPageDataAfterBranchSwitchSaga);
  yield debounce(350, A.setResourceSearch, reloadAssignedResourcesSaga);
}
